/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import cuoreAnatomico from '../assets/img/cuore-anatomico.png';

const schema = yup.object({
  username: yup.string().required('Username è obbligatorio'),
  password: yup.string().required('Password è obbligatoria'),
}).required();

type LoginFormData = yup.InferType<typeof schema>;

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [lastError, setLastError] = useState<any>(null);

  // Redirect alla dashboard se l'utente è già autenticato
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      console.log('🔄 [LOGIN] Utente già autenticato, redirect alla dashboard');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Controlla se ci sono errori salvati all'avvio
  useEffect(() => {
    const savedError = localStorage.getItem('last_login_error');
    if (savedError) {
      console.log('🔍 [DEBUG] Errore trovato nel localStorage all\'avvio:', savedError);
    }
  }, []);

  // Funzione per salvare log nel localStorage
  const saveLog = (message: string, data?: any) => {
    const logs = JSON.parse(localStorage.getItem('debug_logs') || '[]');
    logs.push({
      timestamp: new Date().toISOString(),
      message,
      data
    });
    // Mantieni solo gli ultimi 50 log
    if (logs.length > 50) {
      logs.splice(0, logs.length - 50);
    }
    localStorage.setItem('debug_logs', JSON.stringify(logs));
  };

  // Funzione per visualizzare l'ultimo errore salvato
  const showLastError = () => {
    const savedError = localStorage.getItem('last_login_error');
    const savedLogs = localStorage.getItem('debug_logs');
    
    if (savedError) {
      const parsedError = JSON.parse(savedError);
      setLastError(parsedError);
      setShowDebug(true);
      console.log('🔍 [DEBUG] Ultimo errore salvato:', parsedError);
    }
    
    if (savedLogs) {
      console.log('🔍 [DEBUG] Log salvati:', JSON.parse(savedLogs));
    }
    
    if (!savedError && !savedLogs) {
      console.log('🔍 [DEBUG] Nessun errore o log salvato trovato');
    }
  };

  // Funzione per pulire i log di debug
  const clearDebugLogs = () => {
    localStorage.removeItem('last_login_error');
    setLastError(null);
    setShowDebug(false);
    console.log('🧹 [DEBUG] Log di debug puliti');
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormData, event?: React.BaseSyntheticEvent) => {
    // Prevenire qualsiasi refresh della pagina
    if (event) {
      event.preventDefault();
    }
    
    console.log('📝 [LOGIN] onSubmit chiamato - Form inviato con dati:', data);
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('📝 [LOGIN] Chiamata funzione login()');
      await login(data);
      console.log('✅ [LOGIN] Login riuscito, navigazione verso dashboard');
      navigate('/');
    } catch (err: any) {
      console.error('❌ [LOGIN] Errore durante il login:', err);
      
      // Salvare log dell'errore per debug
      saveLog('❌ [LOGIN] Errore durante il login:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Salvare l'errore nel localStorage per debug
      localStorage.setItem('last_login_error', JSON.stringify({
        timestamp: new Date().toISOString(),
        error: {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          config: err.config
        }
      }));
      
      setError(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      console.log('🏁 [LOGIN] Fine processo login, loading:', false);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-hero" aria-hidden="true">
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <filter id="heart-duotone" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0 0 0 1 0"
            />
            <feComponentTransfer>
              <feFuncR type="table" tableValues="0.45 1" />
              <feFuncG type="table" tableValues="0.04 0.78" />
              <feFuncB type="table" tableValues="0.14 0.82" />
            </feComponentTransfer>
          </filter>
        </svg>
        <div className="login-hero-glow" />
        <img src={cuoreAnatomico} alt="" className="login-hero-heart" />
        <svg className="login-hero-ecg" viewBox="0 0 600 80" preserveAspectRatio="none">
          <polyline
            points="0,40 120,40 150,40 165,10 185,70 205,25 220,40 340,40 370,40 385,10 405,70 425,25 440,40 600,40"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        <div className="login-hero-text">
          <h1>Cardio<span>Ref</span></h1>
          <p>Referti e fatture del tuo studio, in un unico posto.</p>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-container">
          <Card className="login-card">
            <Card.Body className="p-4 p-md-5">
              <div className="login-header mb-4">
                <h2 className="fw-bold mb-1">Bentornato</h2>
                <p className="text-muted mb-0">Accedi al tuo account per continuare</p>
              </div>

              {error && (
                <Alert variant="danger" className="modern-alert mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="error-icon">⚠️</div>
                    <strong>Errore di accesso</strong>
                  </div>
                  {error}
                  <hr />
                  <div className="mt-3 d-flex gap-2">
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={showLastError}
                      className="btn-sm-modern"
                    >
                      🔍 Dettagli Errore
                    </Button>
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      onClick={() => {
                        const logs = localStorage.getItem('debug_logs');
                        if (logs) {
                          console.log('📋 [DEBUG] Tutti i log salvati:', JSON.parse(logs));
                        }
                      }}
                      className="btn-sm-modern"
                    >
                      📋 Log Completi
                    </Button>
                  </div>
                </Alert>
              )}

              {showDebug && lastError && (
                <Alert variant="warning" className="modern-alert mb-4">
                  <h6 className="d-flex align-items-center gap-2">
                    🔍 Debug - Ultimo Errore
                  </h6>
                  <pre className="debug-pre">
                    {JSON.stringify(lastError, null, 2)}
                  </pre>
                  <div className="mt-3 d-flex gap-2">
                    <Button 
                      variant="outline-warning" 
                      size="sm" 
                      onClick={() => setShowDebug(false)}
                      className="btn-sm-modern"
                    >
                      Nascondi
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={clearDebugLogs}
                      className="btn-sm-modern"
                    >
                      🧹 Pulisci Log
                    </Button>
                  </div>
                </Alert>
              )}

              <Form 
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="modern-form"
              >
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Username</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Inserisci username"
                    {...register('username')}
                    isInvalid={!!errors.username}
                    className="form-control-lg"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Inserisci password"
                    {...register('password')}
                    isInvalid={!!errors.password}
                    className="form-control-lg"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-100 btn-modern btn-lg d-flex align-items-center justify-content-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="modern-spinner" style={{ width: '20px', height: '20px' }}></div>
                      Accesso in corso...
                    </>
                  ) : (
                    <>
                      <LogIn size={20} />
                      Accedi
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Login; 